const getDataLayer = () => {
  if (typeof window === "undefined") return null;

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const pushEvent = (payload) => {
  const dataLayer = getDataLayer();

  if (!dataLayer || !payload?.event) return;

  dataLayer.push(payload);
};

const trackPageView = (to) => {
  if (typeof window === "undefined") return;

  pushEvent({
    event: "page_view",
    page_title: document.title,
    page_path: to.fullPath,
    page_location: window.location.href,
    route_name: to.name || undefined,
  });
};

const ASSET_TYPE_MAP = {
  BIM: '3D',
  REV: '3D',
  RVT: '3D',
  '3DF': '3D',
  '2DF': 'Archivos 2D',
  TIN: 'Ficha Técnica',
  CTF: 'Certificados',
  CTP: 'Certificados',
  CTV: 'Certificados',
  EPD: 'EPD',
  SCS: 'SCS',
  PCT: 'Catálogos de producto',
  CTG: 'Catálogos de producto',
  GIS: 'Galería',
  TEX: 'Textiles y Acabados',
  TAB: 'Textiles y Acabados',
  SAM: 'Textiles y Acabados',
  GAR: 'Garantías',
  GRT: 'Garantías',
  HTU: 'Instrucciones de montaje',
  INS: 'Instrucciones de montaje',
  MNT: 'Mantenimiento',
  PEP: 'PEP',
  PRL: 'Price list',
  PRI: 'Price list',
  LGS: 'Logos',
  VID: 'Videos',
};

const FORMAT_ASSET_TYPE_MAP = {
  BIM: '3D',
  REVIT: '3D',
  RVT: '3D',
  '3DS': '3D',
  OBJ: '3D',
  FBX: '3D',
  IFC: '3D',
  DWG: 'Archivos 2D',
  '2D': 'Archivos 2D',
};

const ACF_TIPO_ASSET_TYPE_MAP = {
  galeria: 'Galería',
  galeria3d: '3D',
  spaces: 'Archivos 2D',
  epd: 'EPD',
};

const TAXONOMY_NAME_ASSET_TYPE_RULES = [
  { regex: /ficha\s*tec|technical/i, value: 'Ficha Técnica' },
  { regex: /certif/i, value: 'Certificados' },
  { regex: /\bepd\b/i, value: 'EPD' },
  { regex: /\bscs\b/i, value: 'SCS' },
  { regex: /catalog|catalogo/i, value: 'Catálogos de producto' },
  { regex: /galer|gallery/i, value: 'Galería' },
  { regex: /textil|acabado|fabric|finish/i, value: 'Textiles y Acabados' },
  { regex: /garant/i, value: 'Garantías' },
  { regex: /instru|how\s*to\s*use|assembly/i, value: 'Instrucciones de montaje' },
  { regex: /mantenimiento|maintenance/i, value: 'Mantenimiento' },
  { regex: /\bpep\b/i, value: 'PEP' },
  { regex: /price\s*list|tarifa/i, value: 'Price list' },
  { regex: /logo/i, value: 'Logos' },
  { regex: /video/i, value: 'Videos' },
];

const stripHtml = (value = '') => String(value).replace(/<[^>]+>/g, '').trim();

const normalizeSpaces = (value = '') => stripHtml(value).replace(/\s+/g, ' ').trim();

const normalizeUpper = (value = '') => normalizeSpaces(value).toUpperCase();

const getCurrentPath = () => {
  if (typeof window === 'undefined') return '';
  return window.location?.pathname || '';
};

const TECHNICAL_TAGS = new Set([
  '2d',
  '3d',
  '3ds',
  'dwg',
  'revit',
  'bim',
  'rfa',
  'ifc',
  'obj',
  'fbx',
  'jpg',
  'jpeg',
  'png',
  'tif',
  'tiff',
  'pdf',
  'zip',
  'catalogo',
  'catalog',
  'catalogue',
  'product catalogue',
  'galeria',
  'gallery',
]);

const isTechnicalTag = (tag) => {
  const normalized = normalizeSpaces(tag).toLowerCase();
  if (!normalized) return true;
  if (TECHNICAL_TAGS.has(normalized)) return true;
  if (/^\d+(ppp|dpi)$/.test(normalized)) return true;
  return false;
};

const getCategoryStoreCategories = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = localStorage.getItem('categoryStore');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.categories) ? parsed.categories : [];
  } catch {
    return [];
  }
};

const getBySpacesStoreItems = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = localStorage.getItem('bySpacesStore');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.bySpaces) ? parsed.bySpaces : [];
  } catch {
    return [];
  }
};

const getFileTypeStoreItems = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = localStorage.getItem('fileTypeStore');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.fileTypes) ? parsed.fileTypes : [];
  } catch {
    return [];
  }
};

const getMarketingMaterialStoreItems = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = localStorage.getItem('marketingMaterialStore');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.marketingMaterials) ? parsed.marketingMaterials : [];
  } catch {
    return [];
  }
};

const getCategorySlugFromPath = () => {
  const path = getCurrentPath();
  const match = path.match(/\/categories\/([^/]+)/i);
  return match?.[1] ? normalizeSpaces(match[1]).toLowerCase() : '';
};

const getBySpacesSlugFromPath = () => {
  const path = getCurrentPath();
  const match = path.match(/\/by-spaces\/([^/]+)/i);
  return match?.[1] ? normalizeSpaces(match[1]).toLowerCase() : '';
};

const getFileTypeSlugFromPath = () => {
  const path = getCurrentPath();
  const match = path.match(/\/file-types\/([^/]+)/i);
  return match?.[1] ? normalizeSpaces(match[1]).toLowerCase() : '';
};

const getMarketingMaterialSlugFromPath = () => {
  const path = getCurrentPath();
  const match = path.match(/\/marketing-materials\/([^/]+)/i);
  return match?.[1] ? normalizeSpaces(match[1]).toLowerCase() : '';
};

const getCategoryNameBySlug = (slug) => {
  if (!slug) return '';
  const categories = getCategoryStoreCategories();
  const matched = categories.find(
    (category) => normalizeSpaces(category?.slug || '').toLowerCase() === slug
  );
  return normalizeSpaces(matched?.name || '');
};

const getCategoryNameById = (id) => {
  if (id === undefined || id === null) return '';
  const categories = getCategoryStoreCategories();
  const matched = categories.find((category) => Number(category?.id) === Number(id));
  return normalizeSpaces(matched?.name || '');
};

const getBySpacesNameBySlug = (slug) => {
  if (!slug) return '';
  const bySpacesItems = getBySpacesStoreItems();
  const matched = bySpacesItems.find(
    (item) => normalizeSpaces(item?.slug || '').toLowerCase() === slug
  );
  return normalizeSpaces(matched?.name || '');
};

const getFileTypeNameBySlug = (slug) => {
  if (!slug) return '';
  const fileTypes = getFileTypeStoreItems();
  const matched = fileTypes.find(
    (item) => normalizeSpaces(item?.slug || '').toLowerCase() === slug
  );
  return normalizeSpaces(matched?.name || '');
};

const getMarketingMaterialNameBySlug = (slug) => {
  if (!slug) return '';
  const marketingMaterials = getMarketingMaterialStoreItems();
  const matched = marketingMaterials.find(
    (item) => normalizeSpaces(item?.slug || '').toLowerCase() === slug
  );
  return normalizeSpaces(matched?.name || '');
};

const deriveMenuSection = () => {
  const path = getCurrentPath();
  const menuEntries = [
    { marker: '/categories/', value: 'ByCategory' },
    { marker: '/file-types/', value: 'ByFiletype' },
    { marker: '/by-spaces/', value: 'BySpaces' },
    { marker: '/marketing-materials/', value: 'ByMarketingMaterial' },
  ]
    .map((entry) => ({ ...entry, index: path.indexOf(entry.marker) }))
    .filter((entry) => entry.index >= 0)
    .sort((left, right) => left.index - right.index);

  return menuEntries[0]?.value || '';
};

const deriveMenuCategory = (menuSection) => {
  switch (menuSection) {
    case 'ByCategory':
      return getCategoryNameBySlug(getCategorySlugFromPath());
    case 'ByFiletype':
      return getFileTypeNameBySlug(getFileTypeSlugFromPath());
    case 'BySpaces':
      return getBySpacesNameBySlug(getBySpacesSlugFromPath());
    case 'ByMarketingMaterial':
      return getMarketingMaterialNameBySlug(getMarketingMaterialSlugFromPath());
    default:
      return '';
  }
};

const deriveSpacesCategory = () => getBySpacesNameBySlug(getBySpacesSlugFromPath());

const deriveProductName = (resource) => {
  const acfModel = Array.isArray(resource.acf?.modelos)
    ? resource.acf.modelos.find(
        (model) => normalizeSpaces(model?.post_title || model?.name) !== ''
      )
    : undefined;

  if (acfModel) return normalizeSpaces(acfModel.post_title || acfModel.name);

  return '';
};

const deriveProductCategory = (resource, product, menuSection) => {
  if (menuSection !== 'ByCategory') return '';

  const explicitCategory = normalizeSpaces(resource.categoryName || '');
  if (explicitCategory && !isTechnicalTag(explicitCategory)) return explicitCategory;

  const byResourceId = Array.isArray(resource.product_tipo)
    ? resource.product_tipo
        .map((categoryId) => getCategoryNameById(categoryId))
        .find((categoryName) => categoryName && !isTechnicalTag(categoryName))
    : '';

  if (byResourceId) return byResourceId;

  const slugCategoryName = getCategoryNameBySlug(getCategorySlugFromPath());
  if (slugCategoryName && !isTechnicalTag(slugCategoryName)) return slugCategoryName;

  const tags = Array.isArray(resource.tag_modelo_names)
    ? resource.tag_modelo_names
        .map((name) => normalizeSpaces(name))
        .filter((name) => name !== '')
    : [];

  if (tags.length === 0) return '';

  const productLower = normalizeSpaces(product).toLowerCase();
  const categoryFromTags = tags.find((tag) => {
    const normalized = tag.toLowerCase();
    if (normalized === productLower) return false;
    if (isTechnicalTag(normalized)) return false;
    return true;
  });

  if (categoryFromTags) return categoryFromTags;

  return '';
};

const deriveAssetType = (resource, format, fileName) => {
  const fileExtension = fileName?.includes('.')
    ? fileName.split('.').pop()
    : '';

  const formatTokens = [format, fileExtension]
    .filter(Boolean)
    .join(',')
    .split(',')
    .map((token) => normalizeUpper(token))
    .filter((token) => token !== '');

  const mappedFromFormat = formatTokens
    .map((token) => FORMAT_ASSET_TYPE_MAP[token])
    .filter(Boolean);

  if (mappedFromFormat.length > 0) {
    const unique = [...new Set(mappedFromFormat)];
    if (unique.length === 1) return unique[0];
    if (unique.includes('3D')) return '3D';
    if (unique.includes('Archivos 2D')) return 'Archivos 2D';
    return unique[0];
  }

  const idAp = normalizeUpper(resource.tipo_recurso_extras?.[0]?.id_ap || '');
  if (idAp && ASSET_TYPE_MAP[idAp]) return ASSET_TYPE_MAP[idAp];

  const acfTipo = normalizeSpaces(resource.acf?.tipo || '').toLowerCase();
  if (acfTipo && ACF_TIPO_ASSET_TYPE_MAP[acfTipo]) return ACF_TIPO_ASSET_TYPE_MAP[acfTipo];

  const taxonomyName = normalizeSpaces(resource.tipo_recurso_extras?.[0]?.name || '');
  if (taxonomyName) {
    const rule = TAXONOMY_NAME_ASSET_TYPE_RULES.find((entry) => entry.regex.test(taxonomyName));
    if (rule) return rule.value;
  }

  return '';
};

const buildDownloadPayload = (resource, { format, fileName } = {}) => {
  if (!resource) return {};

  const title = normalizeSpaces(resource.title?.rendered || '');
  const asset_type = deriveAssetType(resource, format, fileName);
  const product = deriveProductName(resource);
  const menu_section = deriveMenuSection();
  const product_category = deriveProductCategory(resource, product, menu_section);
  const menu_category = deriveMenuCategory(menu_section);
  const spaces_category = deriveSpacesCategory();

  let file_name = '';
  let file_extension = '';

  if (fileName) {
    const dotIndex = fileName.lastIndexOf('.');
    file_extension = dotIndex >= 0 ? fileName.slice(dotIndex + 1).toLowerCase() : '';
    file_name = dotIndex >= 0 ? fileName.slice(0, dotIndex) : fileName;
  } else if (format) {
    file_extension = format.toLowerCase().replace('tif', 'tiff');
    file_name = title;
  } else if (resource.acf?.recurso?.filename) {
    const fn = resource.acf.recurso.filename;
    const dotIndex = fn.lastIndexOf('.');
    file_extension = dotIndex >= 0 ? fn.slice(dotIndex + 1).toLowerCase() : '';
    file_name = dotIndex >= 0 ? fn.slice(0, dotIndex) : fn;
  } else {
    file_name = title;
  }

  return {
    file_name,
    file_extension,
    asset_type,
    product,
    product_category,
    menu_section,
    menu_category,
    spaces_category,
  };
};

const trackDownload = (payload) => {
  pushEvent({
    event: "download",
    event_category: "downloads",
    ...payload,
  });
};

export default {
  pushEvent,
  trackPageView,
  trackDownload,
  buildDownloadPayload,
};